import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Table,
  Badge,
  Button,
  Input,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { fetchBookings, fetchBookingDetails } from "../../slices/bookings/thunk";
import { fetchFarmhouses } from "../../slices/farmhouse/thunk";
import { clearBookingDetails } from "../../slices/bookings/reducer";
import dayjs from "dayjs";

const BookingSheet = () => {
  const dispatch = useDispatch();

  // 1. Fetch Redux States
  const { farmhouses, bookings, loading, bookingDetails } = useSelector((state) => ({
    farmhouses: state.Farmhouse.farmhouses || [],
    bookings: state.Bookings.bookings || [],
    loading: state.Bookings.loading || state.Farmhouse.loading,
    bookingDetails: state.Bookings.bookingDetails,
  }));

  // 2. Local State
  const [selectedFarmhouse, setSelectedFarmhouse] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1); // 1-indexed (Jan = 1)
  const [activeTab, setActiveTab] = useState(null); // Selected Farmhouse ID

  // Booking details modal state
  const [detailsModal, setDetailsModal] = useState(false);
  const [activeBookingId, setActiveBookingId] = useState(null);

  // Inline popover state — stores { booking, cellKey } where cellKey = `${dateStr}-${DAY|NIGHT}`
  const [activePop, setActivePop] = useState(null);

  // 3. Load Initial Data
  useEffect(() => {
    dispatch(fetchFarmhouses({ limit: 100 }));
  }, [dispatch]);

  // Set default selected farmhouse when loaded
  useEffect(() => {
    if (farmhouses.length > 0 && !activeTab) {
      setActiveTab(farmhouses[0].id);
      setSelectedFarmhouse(farmhouses[0]);
    }
  }, [farmhouses, activeTab]);

  // Fetch bookings for current month date range
  const loadBookingsForMonth = useCallback(() => {
    const daysInMonth = dayjs(`${selectedYear}-${String(selectedMonth).padStart(2, "0")}-01`).daysInMonth();
    const dateFrom = `${selectedYear}-${String(selectedMonth).padStart(2, "0")}-01`;
    const dateTo = `${selectedYear}-${String(selectedMonth).padStart(2, "0")}-${String(daysInMonth).padStart(2, "0")}`;

    dispatch(
      fetchBookings({
        page: 1,
        limit: 500, // retrieve all bookings for this month
        dateFrom,
        dateTo,
      })
    );
  }, [dispatch, selectedYear, selectedMonth]);

  useEffect(() => {
    loadBookingsForMonth();
  }, [loadBookingsForMonth]);

  // Handle farmhouse change
  const handleFarmhouseSelect = (farm) => {
    setActiveTab(farm.id);
    setSelectedFarmhouse(farm);
  };

  // Month navigation helpers
  const handlePrevMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const getMonthName = (monthNum) => {
    return dayjs().month(monthNum - 1).format("MMMM");
  };

  // Helper to parse dates & times for booking overlap checks
  const getBookingDateTimes = useCallback((booking) => {
    const startDateStr = booking.startDate || booking.bookingDate;
    const endDateStr = booking.endDate || booking.bookingEndDate || startDateStr;
    const checkIn = booking.checkInTime || booking.bookingTimeFrom || "07:00";
    const checkOut = booking.checkOutTime || booking.bookingTimeTo || "18:00";

    const formatTimeToHHmm = (timeStr) => {
      if (!timeStr) return "07:00";
      try {
        if (timeStr.includes("T")) {
          const d = new Date(timeStr);
          if (!isNaN(d)) {
            return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
          }
        }
        let cleanTime = timeStr.trim().toUpperCase();
        let ampm = "";
        if (cleanTime.endsWith("AM")) {
          ampm = "AM";
          cleanTime = cleanTime.replace("AM", "").trim();
        } else if (cleanTime.endsWith("PM")) {
          ampm = "PM";
          cleanTime = cleanTime.replace("PM", "").trim();
        }

        let [hours, minutes] = cleanTime.split(":");
        let h = parseInt(hours, 10);
        let m = parseInt(minutes, 10) || 0;

        if (ampm === "PM" && h < 12) h += 12;
        if (ampm === "AM" && h === 12) h = 0;

        return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      } catch (e) {
        return "07:00";
      }
    };

    const parsedCheckIn = formatTimeToHHmm(checkIn);
    const parsedCheckOut = formatTimeToHHmm(checkOut);

    const startOnly = String(startDateStr).split("T")[0];
    const endOnly = String(endDateStr).split("T")[0];

    const start = new Date(`${startOnly}T${parsedCheckIn}:00`);
    const end = new Date(`${endOnly}T${parsedCheckOut}:00`);

    return { start, end };
  }, []);

  // 4. Generate Calendar Days & Slot Overlaps
  const calendarSheetData = useMemo(() => {
    if (!activeTab) return [];

    // Generate dates list for selected month/year
    const daysInMonth = dayjs(`${selectedYear}-${String(selectedMonth).padStart(2, "0")}-01`).daysInMonth();
    const daysList = [];

    // Filter bookings only for active farmhouse
    const farmBookings = bookings.filter((b) => {
      const bFarmId = b.farmhouse?.id || b.farmhouse?._id || b.farmhouseId;
      return bFarmId && bFarmId.toString() === activeTab?.toString();
    });

    for (let i = 1; i <= daysInMonth; i++) {
      const dateStr = `${selectedYear}-${String(selectedMonth).padStart(2, "0")}-${String(i).padStart(2, "0")}`;
      const nextDateStr = dayjs(dateStr).add(1, "day").format("YYYY-MM-DD");
      const dateObj = dayjs(dateStr);

      const daySlotStart = new Date(`${dateStr}T09:00:00`);
      const daySlotEnd = new Date(`${dateStr}T18:00:00`);
      const nightSlotStart = new Date(`${dateStr}T18:00:00`);
      const nightSlotEnd = new Date(`${nextDateStr}T09:00:00`);

      let dayBooking = null;
      let nightBooking = null;

      // Find overlapping bookings for both DAY and NIGHT slots
      farmBookings.forEach((booking) => {
        if (booking.paymentStatus === "cancel") return;

        try {
          const { start: bStart, end: bEnd } = getBookingDateTimes(booking);

          // DAY slot overlap check
          const dayOverlapStart = Math.max(bStart.getTime(), daySlotStart.getTime());
          const dayOverlapEnd = Math.min(bEnd.getTime(), daySlotEnd.getTime());
          if (dayOverlapStart < dayOverlapEnd) {
            dayBooking = booking;
          }

          // NIGHT slot overlap check
          const nightOverlapStart = Math.max(bStart.getTime(), nightSlotStart.getTime());
          const nightOverlapEnd = Math.min(bEnd.getTime(), nightSlotEnd.getTime());
          if (nightOverlapStart < nightOverlapEnd) {
            nightBooking = booking;
          }
        } catch (err) {
          console.error("Error evaluating overlap for booking ID: " + booking.id, err);
        }
      });

      daysList.push({
        dayNum: i,
        dateStr,
        dayName: dateObj.format("dddd"),
        isWeekend: dateObj.day() === 0 || dateObj.day() === 6,
        dayBooking,
        nightBooking,
      });
    }

    return daysList;
  }, [activeTab, bookings, selectedYear, selectedMonth, getBookingDateTimes]);

  // Split calendar days into 2 columns (e.g. Days 1-15, Days 16-31) to prevent scrolling
  const calendarColumns = useMemo(() => {
    const mid = Math.ceil(calendarSheetData.length / 2);
    return {
      col1: calendarSheetData.slice(0, mid),
      col2: calendarSheetData.slice(mid),
    };
  }, [calendarSheetData]);

  // 5. Booking Click Handlers
  // Opens inline popover — if same cell clicked again, toggle close
  const handleCellClick = (booking, cellKey) => {
    if (!booking) return;
    if (activePop?.cellKey === cellKey) {
      setActivePop(null);
    } else {
      setActivePop({ booking, cellKey });
    }
  };

  // Open full details modal from popover
  const handleViewDetails = (booking) => {
    const bookingId = booking.id || booking._id;
    setActivePop(null);
    setActiveBookingId(bookingId);
    dispatch(fetchBookingDetails(bookingId));
    setDetailsModal(true);
  };

  // Format and show payment status badge
  const renderPaymentStatusBadge = (status) => {
    const s = (status || "incomplete").toLowerCase();
    switch (s) {
      case "paid":
        return <Badge color="success">Paid</Badge>;
      case "partial":
        return <Badge color="warning">Outstanding</Badge>;
      case "incomplete":
        return <Badge color="danger">Incomplete</Badge>;
      case "cancel":
        return <Badge color="dark">Cancelled</Badge>;
      default:
        return <Badge color="secondary">{s.toUpperCase()}</Badge>;
    }
  };

  return (
    <div className="page-content">
      {/* Dynamic Styling block */}
      <style dangerouslySetInnerHTML={{
        __html: `
        /* click outside popover closes it */
        .bs-pop-overlay {
          position: fixed;
          inset: 0;
          z-index: 1000;
          background: transparent;
        }
        .bs-pop-card {
          position: absolute;
          z-index: 1050;
          top: calc(100% + 4px);
          left: 50%;
          transform: translateX(-50%);
          min-width: 230px;
          max-width: 280px;
          background: #ffffff;
          border-radius: 10px;
          box-shadow: 0 8px 24px rgba(0,0,0,0.15);
          border: 1px solid #e2e8f0;
          padding: 12px 14px;
          font-size: 0.8rem;
          text-align: left;
          animation: popFade 0.12s ease;
        }
        @keyframes popFade {
          from { opacity: 0; transform: translateX(-50%) translateY(-4px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
        .bs-pop-card::before {
          content: '';
          position: absolute;
          top: -6px;
          left: 50%;
          transform: translateX(-50%) rotate(45deg);
          width: 10px;
          height: 10px;
          background: #ffffff;
          border-left: 1px solid #e2e8f0;
          border-top: 1px solid #e2e8f0;
        }
        .bs-pop-row {
          display: flex;
          gap: 6px;
          margin-bottom: 5px;
          align-items: flex-start;
        }
        .bs-pop-label {
          font-weight: 600;
          color: #64748b;
          min-width: 68px;
          flex-shrink: 0;
        }
        .bs-pop-value {
          color: #1e293b;
          font-weight: 500;
          word-break: break-word;
        }
        .bs-pop-divider { border-top: 1px solid #f1f5f9; margin: 8px 0; }
        .bs-tick {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          font-size: 1.1rem;
          cursor: pointer;
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        .bs-tick:hover { transform: scale(1.15); box-shadow: 0 2px 8px rgba(0,0,0,0.15); }
        .legend-tick {
          pointer-events: none !important;
          cursor: default !important;
        }
        .legend-tick:hover {
          transform: none !important;
          box-shadow: none !important;
        }
        .bs-tick-paid    { background: #d1fae5; color: #059669; }
        .bs-tick-partial { background: #fef3c7; color: #d97706; }
        .bs-tick-incomplete { background: #fee2e2; color: #dc2626; }
        .bs-tick-default { background: #e0e7ff; color: #4338ca; }
        .booking-sheet-date-td {
          white-space: nowrap;
          font-size: 12px;
          font-weight: 500;
          padding: 5px 6px !important;
        }
        .booking-sheet-table {
          border-collapse: collapse;
          border-spacing: 0;
          font-size: 12px;
          width: 100% !important;
          min-width: 500px;
        }
        .booking-sheet-table th {
          background-color: #00209F !important;
          color: #ffffff !important;
          font-weight: 600;
          text-align: center;
          padding: 8px 6px;
          border: 1px solid #001a80;
          font-size: 13px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .booking-sheet-table td {
          padding: 5px 5px;
          vertical-align: middle;
          border: 1px solid #e2e8f0;
          font-size: 12px;
        }
        .booking-sheet-row-weekend {
          background-color: #fffbeb !important;
        }
        .booking-sheet-row-weekend td:first-child,
        .booking-sheet-row-weekend td:nth-child(2) {
          background-color: #fef3c7 !important;
          font-weight: 600;
        }
        .booking-sheet-cell-slot {
          text-align: center;
          position: relative;
          height: 46px;
          overflow: visible !important;
        }
        .booking-sheet-cell-slot.booked {
          cursor: pointer;
          transition: background-color 0.15s ease;
        }
        .booking-sheet-cell-slot.booked:hover {
          background-color: #f1f5f9;
        }
        
        .booked-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 4px 8px;
          border-radius: 6px;
          font-weight: 600;
          font-size: 0.75rem;
          max-width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
        }
        .booked-badge-paid {
          background-color: #d1fae5;
          color: #065f46;
          border: 1px solid #a7f3d0;
        }
        .booked-badge-partial {
          background-color: #fef3c7;
          color: #92400e;
          border: 1px solid #fde68a;
        }
        .booked-badge-incomplete {
          background-color: #fee2e2;
          color: #991b1b;
          border: 1px solid #fca5a5;
        }
        
        .legend-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          display: inline-block;
          margin-right: 6px;
        }
        .legend-dot-day {
          background-color: #38bdf8;
        }
        .legend-dot-night {
          background-color: #818cf8;
        }

        /* --- SAMSUNG FOLD & MOBILE ADAPTATIONS --- */
        @media (max-width: 480px) {
          .page-content {
            padding: 12px 4px !important;
          }
          .booking-sheet-table {
            font-size: 10px !important;
            width: 100% !important;
          }
          .booking-sheet-table th {
            padding: 5px 3px !important;
            font-size: 11px !important;
          }
          .booking-sheet-table td {
            padding: 4px 3px !important;
            font-size: 10px !important;
          }
          .booking-sheet-date-td {
            font-size: 10px !important;
          }
          .booking-sheet-cell-slot {
            height: 36px !important;
          }
          .bs-tick {
            width: 24px !important;
            height: 24px !important;
            font-size: 1rem !important;
          }
          .mobile-header-stack {
            flex-direction: column;
            align-items: flex-start !important;
            gap: 12px !important;
          }
          .mobile-nav-stack {
            width: 100%;
            justify-content: space-between !important;
            margin-top: 8px;
          }
          .mobile-title {
            font-size: 1.1rem !important;
          }
        }

        @media (min-width: 481px) and (max-width: 991px) {
          .booking-sheet-table {
            font-size: 11px !important;
            width: 100% !important;
          }
          .booking-sheet-table th {
            padding: 5px 6px !important;
            font-size: 13px !important;
          }
          .booking-sheet-table td {
            padding: 5px 6px !important;
            font-size: 11px !important;
          }
          .booking-sheet-date-td {
            font-size: 11px !important;
          }
          .booking-sheet-cell-slot {
            height: 42px !important;
          }
          .mobile-title {
            font-size: 1.3rem !important;
          }
        }
      ` }} />

      <Container fluid>
        <BreadCrumb title="Booking Sheet" pageTitle="Bookings" />

        {/* --- SECTION 1: FARMHOUSE DROPDOWN & LEGEND --- */}
        <Row className="mb-3">
          <Col xs={12} md={6} className="mb-2">
            <div className="px-3 py-2 bg-white rounded shadow-sm border border-light">
              <span className="fw-semibold text-muted flex-shrink-0" style={{ fontSize: "11px" }}>
                <i className="ri-home-line me-1 text-primary"></i>Farmhouse:
              </span>
              {farmhouses.length === 0 ? (
                <div className="d-flex align-items-center">
                  <Spinner size="sm" className="me-2" color="primary" />
                  <span className="text-muted" style={{ fontSize: "11px" }}>Loading...</span>
                </div>
              ) : (
                <Input
                  type="select"
                  id="farmhouse-select"
                  className="form-select form-select-sm"
                  value={activeTab || ""}
                  onChange={(e) => {
                    const farmId = e.target.value;
                    const selected = farmhouses.find((f) => f.id.toString() === farmId.toString());
                    if (selected) handleFarmhouseSelect(selected);
                  }}
                  style={{ fontWeight: "500", color: "#00209F", border: "1.2px solid #00209F", fontSize: "12px", height: "30px", padding: "0 8px" }}
                >
                  {farmhouses.map((farm) => (
                    <option key={farm.id} value={farm.id}>{farm.name}</option>
                  ))}
                </Input>
              )}
            </div>
          </Col>
          <Col xs={12} md={6}>
            <div className="d-flex flex-wrap align-items-center gap-2 px-3 py-2 bg-white rounded shadow-sm border border-light overflow-hidden">
              <span className="fw-semibold text-muted flex-shrink-0" style={{ fontSize: "11px" }}>Legend:</span>
              <div className="d-flex align-items-center gap-1 flex-shrink-0">
                <span className="bs-tick bs-tick-paid legend-tick" style={{ width: "20px", height: "20px", fontSize: "0.8rem" }}>
                  <i className="ri-checkbox-circle-fill"></i>
                </span>
                <span className="text-dark fw-medium" style={{ fontSize: "11px", whiteSpace: "nowrap" }}>Paid</span>
              </div>
              <div className="d-flex align-items-center gap-1 flex-shrink-0">
                <span className="bs-tick bs-tick-partial legend-tick" style={{ width: "20px", height: "20px", fontSize: "0.8rem" }}>
                  <i className="ri-checkbox-circle-fill"></i>
                </span>
                <span className="text-dark fw-medium" style={{ fontSize: "11px", whiteSpace: "nowrap" }}>Outstanding</span>
              </div>
              <div className="d-flex align-items-center gap-1 flex-shrink-0">
                <span className="bs-tick bs-tick-incomplete legend-tick" style={{ width: "20px", height: "20px", fontSize: "0.8rem" }}>
                  <i className="ri-checkbox-circle-fill"></i>
                </span>
                <span className="text-dark fw-medium" style={{ fontSize: "11px", whiteSpace: "nowrap" }}>Incomplete</span>
              </div>
            </div>
          </Col>
        </Row>

        {/* --- SECTION 2: MONTH NAVIGATION & HEADER --- */}
        <Row className="mb-2 lg:mb-4 align-items-center mobile-header-stack">
          <Col md={6} className="d-flex align-items-center gap-3">
            <h5 className="mb-0 fw-bold d-flex align-items-center mobile-title" style={{ color: "#00209F", fontSize: "16px" }}>
              <i className="ri-calendar-2-line me-2"></i>
              {selectedFarmhouse ? selectedFarmhouse.name : "Silver Villa"}
            </h5>
            {loading && <Spinner size="sm" color="primary" />}
          </Col>
          <Col md={6} className="d-flex justify-content-md-end align-items-center gap-2 mt-3 mt-md-0 mobile-nav-stack">
            <Button color="light" className="btn-icon" onClick={handlePrevMonth}>
              <i className="ri-arrow-left-s-line fs-16"></i>
            </Button>
            <span className="fs-15 fw-bold px-3 text-center" style={{ minWidth: "120px", color: "#1e293b" }}>
              {getMonthName(selectedMonth)} {selectedYear}
            </span>
            <Button color="light" className="btn-icon" onClick={handleNextMonth}>
              <i className="ri-arrow-right-s-line fs-16"></i>
            </Button>
            <Button
              color="primary"
              className="ms-2"
              onClick={() => {
                const today = new Date();
                setSelectedMonth(today.getMonth() + 1);
                setSelectedYear(today.getFullYear());
              }}
            >
              Today
            </Button>
          </Col>
        </Row>

        {/* --- SECTION 3: CALENDAR SHEET GRID (SINGLE COLUMN) --- */}
        <Row>
          <Col xs={12} className="mb-4">
            <Card className="shadow-sm border-0">
              <CardBody className="p-0">
                <Table responsive className="booking-sheet-table mb-0">
                  <colgroup>
                    <col style={{ width: "22%" }} />
                    <col style={{ width: "13%" }} />
                    <col style={{ width: "32.5%" }} />
                    <col style={{ width: "32.5%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>{getMonthName(selectedMonth).slice(0, 3)}</th>
                      <th>Days</th>
                      <th>DAY (9am-6pm)</th>
                      <th>NIGHT (6pm-9am)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calendarSheetData.map((day) => {
                      const rowClass = day.isWeekend ? "booking-sheet-row-weekend" : "";
                      const formattedDate = dayjs(day.dateStr).format("DD MMM YYYY");
                      const dayKey = `${day.dateStr}-DAY`;
                      const nightKey = `${day.dateStr}-NIGHT`;

                      const tickClass = (status) => {
                        const s = (status || "incomplete").toLowerCase();
                        if (s === "paid") return "bs-tick bs-tick-paid";
                        if (s === "partial") return "bs-tick bs-tick-partial";
                        if (s === "incomplete") return "bs-tick bs-tick-incomplete";
                        return "bs-tick bs-tick-default";
                      };

                      return (
                        <tr key={day.dayNum} className={rowClass}>
                          <td className="booking-sheet-date-td">{formattedDate}</td>
                          <td className="text-capitalize" style={{ whiteSpace: "nowrap", fontSize: "12px" }}>{day.dayName}</td>

                          {/* DAY SLOT CELL */}
                          <td
                            className={`booking-sheet-cell-slot ${day.dayBooking ? "booked" : ""}`}
                            style={{ position: "relative" }}
                            onClick={() => day.dayBooking && handleCellClick(day.dayBooking, dayKey)}
                          >
                            {day.dayBooking ? (
                              <>
                                <span className={tickClass(day.dayBooking.paymentStatus)}>
                                  <i className="ri-checkbox-circle-fill"></i>
                                </span>
                                {activePop?.cellKey === dayKey && (
                                  <>
                                    <div className="bs-pop-overlay" onClick={(e) => { e.stopPropagation(); setActivePop(null); }} />
                                    <div className="bs-pop-card" onClick={(e) => e.stopPropagation()}>
                                      <div className="bs-pop-row"><span className="bs-pop-label">Guest:</span><span className="bs-pop-value">{day.dayBooking.user?.name || "Guest"}</span></div>
                                      <div className="bs-pop-row"><span className="bs-pop-label">Mobile:</span><span className="bs-pop-value">{day.dayBooking.user?.mobileNo || "N/A"}</span></div>
                                      <div className="bs-pop-divider" />
                                      <div className="bs-pop-row"><span className="bs-pop-label">Check-in:</span><span className="bs-pop-value">{dayjs(day.dayBooking.startDate || day.dayBooking.bookingDate).format("DD MMM")} {day.dayBooking.checkInTime || day.dayBooking.bookingTimeFrom || ""}</span></div>
                                      <div className="bs-pop-row"><span className="bs-pop-label">Check-out:</span><span className="bs-pop-value">{dayjs(day.dayBooking.endDate || day.dayBooking.bookingEndDate).format("DD MMM")} {day.dayBooking.checkOutTime || day.dayBooking.bookingTimeTo || ""}</span></div>
                                      <div className="bs-pop-divider" />
                                      <div className="bs-pop-row"><span className="bs-pop-label">Amount:</span><span className="bs-pop-value fw-bold text-primary">₹{day.dayBooking.finalTotal || day.dayBooking.finalPrice || 0}</span></div>
                                      <div className="bs-pop-row"><span className="bs-pop-label">Status:</span><span className="bs-pop-value">{renderPaymentStatusBadge(day.dayBooking.paymentStatus)}</span></div>
                                      <div className="bs-pop-divider" />
                                      <button
                                        className="btn btn-primary btn-sm w-100"
                                        style={{ fontSize: "0.75rem", padding: "4px 0" }}
                                        onClick={() => handleViewDetails(day.dayBooking)}
                                      >
                                        <i className="ri-eye-line me-1" />View Details
                                      </button>
                                    </div>
                                  </>
                                )}
                              </>
                            ) : (
                              <span className="text-muted" style={{ fontSize: "0.85rem" }}>—</span>
                            )}
                          </td>

                          {/* NIGHT SLOT CELL */}
                          <td
                            className={`booking-sheet-cell-slot ${day.nightBooking ? "booked" : ""}`}
                            style={{ position: "relative" }}
                            onClick={() => day.nightBooking && handleCellClick(day.nightBooking, nightKey)}
                          >
                            {day.nightBooking ? (
                              <>
                                <span className={tickClass(day.nightBooking.paymentStatus)}>
                                  <i className="ri-checkbox-circle-fill"></i>
                                </span>
                                {activePop?.cellKey === nightKey && (
                                  <>
                                    <div className="bs-pop-overlay" onClick={(e) => { e.stopPropagation(); setActivePop(null); }} />
                                    <div className="bs-pop-card" onClick={(e) => e.stopPropagation()}>
                                      <div className="bs-pop-row"><span className="bs-pop-label">Guest:</span><span className="bs-pop-value">{day.nightBooking.user?.name || "Guest"}</span></div>
                                      <div className="bs-pop-row"><span className="bs-pop-label">Mobile:</span><span className="bs-pop-value">{day.nightBooking.user?.mobileNo || "N/A"}</span></div>
                                      <div className="bs-pop-divider" />
                                      <div className="bs-pop-row"><span className="bs-pop-label">Check-in:</span><span className="bs-pop-value">{dayjs(day.nightBooking.startDate || day.nightBooking.bookingDate).format("DD MMM")} {day.nightBooking.checkInTime || day.nightBooking.bookingTimeFrom || ""}</span></div>
                                      <div className="bs-pop-row"><span className="bs-pop-label">Check-out:</span><span className="bs-pop-value">{dayjs(day.nightBooking.endDate || day.nightBooking.bookingEndDate).format("DD MMM")} {day.nightBooking.checkOutTime || day.nightBooking.bookingTimeTo || ""}</span></div>
                                      <div className="bs-pop-divider" />
                                      <div className="bs-pop-row"><span className="bs-pop-label">Amount:</span><span className="bs-pop-value fw-bold text-primary">₹{day.nightBooking.finalTotal || day.nightBooking.finalPrice || 0}</span></div>
                                      <div className="bs-pop-row"><span className="bs-pop-label">Status:</span><span className="bs-pop-value">{renderPaymentStatusBadge(day.nightBooking.paymentStatus)}</span></div>
                                      <div className="bs-pop-divider" />
                                      <button
                                        className="btn btn-primary btn-sm w-100"
                                        style={{ fontSize: "0.75rem", padding: "4px 0" }}
                                        onClick={() => handleViewDetails(day.nightBooking)}
                                      >
                                        <i className="ri-eye-line me-1" />View Details
                                      </button>
                                    </div>
                                  </>
                                )}
                              </>
                            ) : (
                              <span className="text-muted" style={{ fontSize: "0.85rem" }}>—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* --- BOOKING DETAILS MODAL --- */}
      <Modal isOpen={detailsModal} toggle={() => { setDetailsModal(false); dispatch(clearBookingDetails()); }} size="lg">
        <ModalHeader style={{ background: "#00209F", color: "#fff", justifyContent: "start", padding: "0.5rem 1rem", position: "relative" }} className="text-white">
          <span style={{ color: "#fff", fontWeight: 500, fontSize: "1rem" }}>
            <i className="ri-calendar-check-line me-2" />
            Booking Details
          </span>
          <button
            onClick={() => { setDetailsModal(false); dispatch(clearBookingDetails()); }}
            style={{ position: "absolute", right: "0.75rem", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#fff", fontSize: "1.25rem", lineHeight: 1, cursor: "pointer", padding: 0 }}
            aria-label="Close"
          >
            &times;
          </button>
        </ModalHeader>
        <ModalBody className="p-2" style={{ overflowX: "hidden", padding: 0 }}>
          {(() => {
            const bookingIdMatches = bookingDetails && (
              (bookingDetails.id && bookingDetails.id.toString() === activeBookingId?.toString()) ||
              (bookingDetails._id && bookingDetails._id.toString() === activeBookingId?.toString())
            );

            if (!bookingDetails || !bookingIdMatches) {
              return (
                <div className="text-center py-4">
                  <Spinner color="primary" />
                  <p className="mt-2 text-muted">Loading booking details...</p>
                </div>
              );
            }

            return (
              <div>
                {/* Guest Info */}
                <div className="mb-3 p-3 rounded" style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
                  <h6 className="fw-bold mb-2" style={{ color: "#00209F" }}><i className="ri-user-line me-1" />Guest Information</h6>
                  <Row>
                    <Col xs={6} md={4}><small className="text-muted">Name</small><div className="fw-medium">{bookingDetails.user?.name || "N/A"}</div></Col>
                    <Col xs={6} md={4}><small className="text-muted">Mobile</small><div className="fw-medium">{bookingDetails.user?.mobileNo || "N/A"}</div></Col>
                    <Col xs={6} md={4}><small className="text-muted">Email</small><div className="fw-medium" style={{ wordBreak: "break-all" }}>{bookingDetails.user?.email || "N/A"}</div></Col>
                  </Row>
                </div>

                {/* Booking Info */}
                <div className="mb-3 p-3 rounded" style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
                  <h6 className="fw-bold mb-2" style={{ color: "#00209F" }}><i className="ri-calendar-2-line me-1" />Booking Info</h6>
                  <Row>
                    <Col xs={6} md={4}><small className="text-muted">Farmhouse</small><div className="fw-medium">{bookingDetails.farmhouse?.name || "N/A"}</div></Col>
                    <Col xs={6} md={4}><small className="text-muted">Check-in</small><div className="fw-medium">{dayjs(bookingDetails.startDate || bookingDetails.bookingDate).format("DD MMM YYYY")} <br /><small>{bookingDetails.checkInTime || bookingDetails.bookingTimeFrom || ""}</small></div></Col>
                    <Col xs={6} md={4}><small className="text-muted">Check-out</small><div className="fw-medium">{dayjs(bookingDetails.endDate || bookingDetails.bookingEndDate).format("DD MMM YYYY")} <br /><small>{bookingDetails.checkOutTime || bookingDetails.bookingTimeTo || ""}</small></div></Col>
                  </Row>
                </div>

                {/* Payment Info */}
                {(() => {
                  const isPaid = bookingDetails.paymentStatus === 'paid';
                  const total = bookingDetails.finalTotal || bookingDetails.finalPrice || 0;
                  const paidAmt = isPaid ? total : (bookingDetails.advancePaid || bookingDetails.paidAmount || 0);
                  const remaining = isPaid ? 0 : (bookingDetails.remainingAmount !== undefined ? bookingDetails.remainingAmount : (total - paidAmt));
                  return (
                    <div className="mb-3 p-3 rounded" style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
                      <h6 className="fw-bold mb-2" style={{ color: "#00209F" }}><i className="ri-money-rupee-circle-line me-1" />Payment Info</h6>
                      <Row>
                        <Col xs={6} md={3}><small className="text-muted">Total Amount</small><div className="fw-bold text-primary">₹{total}</div></Col>
                        <Col xs={6} md={3}><small className="text-muted">Amount Paid</small><div className="fw-medium text-success">₹{paidAmt}</div></Col>
                        <Col xs={6} md={3}><small className="text-muted">Remaining</small><div className={`fw-medium ${remaining > 0 ? 'text-danger' : 'text-success'}`}>₹{remaining}</div></Col>
                        <Col xs={6} md={3}><small className="text-muted">Status</small><div className="mt-1">{renderPaymentStatusBadge(bookingDetails.paymentStatus)}</div></Col>
                      </Row>
                    </div>
                  );
                })()}

                {/* Remarks */}
                {bookingDetails.remarks && (
                  <div className="p-3 rounded" style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
                    <h6 className="fw-bold mb-1" style={{ color: "#00209F" }}><i className="ri-sticky-note-line me-1" />Remarks</h6>
                    <p className="mb-0 text-muted" style={{ fontSize: "0.875rem" }}>{bookingDetails.remarks}</p>
                  </div>
                )}
              </div>
            );
          })()}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => { setDetailsModal(false); dispatch(clearBookingDetails()); }}>Close</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default BookingSheet;
